import type {CreateConnectionDataADto} from '@cohort/admin-schemas/connection';
import type {ConnectionEditComponentProps} from '@cohort/merchants/apps';
import Button from '@cohort/merchants/components/buttons/Button';
import ConnectionHeader from '@cohort/merchants/components/connections/ConnectionHeader';
import Input from '@cohort/merchants/components/form/input/Input';
import {connectionsKeys} from '@cohort/merchants/hooks/api/Connections';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {notify} from '@cohort/merchants/hooks/toast';
import {createConnection} from '@cohort/merchants/lib/api/Connections';
import type {BazaarvoiceCredentials} from '@cohort/shared/apps/bazaarvoice/connector';
import {BazaarvoiceCredentialsSchema} from '@cohort/shared/apps/bazaarvoice/connector';
import {REDACTED_FIELD} from '@cohort/shared/constants';
import {zodResolver} from '@hookform/resolvers/zod';
import {useQueryClient} from '@tanstack/react-query';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

const BazaarvoiceEditConnectionComponent: React.FC<ConnectionEditComponentProps> = ({
  existingConnection: connectionToEdit,
  onClose,
  showCancelBtn,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const queryClient = useQueryClient();
  const {t} = useTranslation('app-bazaarvoice', {
    keyPrefix: 'editConnectionComponent',
  });

  const {register, handleSubmit, control} = useForm<BazaarvoiceCredentials>({
    resolver: zodResolver(BazaarvoiceCredentialsSchema),
    defaultValues: {
      sftpServer: {
        host: connectionToEdit ? REDACTED_FIELD : undefined,
        port: undefined,
        username: connectionToEdit ? REDACTED_FIELD : undefined,
        password: connectionToEdit ? REDACTED_FIELD : undefined,
      },
    },
  });

  const {isLoading, mutate: createOrFixConnectionMutation} = useCohortMutation({
    mutationFn: async (data: CreateConnectionDataADto) => createConnection(merchantId, data),
    notifySuccessMessage: t('createSuccessNotification'),
    onSuccess: async connection => {
      await queryClient.invalidateQueries(connectionsKeys.list(merchantId));
      onClose?.(connection);
    },
    onError: () => notify('error', t('errorInvalidCredentials')),
  });

  return (
    <form
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(async data => {
          createOrFixConnectionMutation({
            connectorId: 'bazaarvoice',
            name: null,
            credentials: {
              sftpServer: {
                host: data.sftpServer.host,
                port: data.sftpServer.port,
                username: data.sftpServer.username,
                password: data.sftpServer.password,
              },
            },
            existingConnectionId: connectionToEdit?.id,
          });
        })(e);
      }}
      className="space-y-6 p-6"
      data-testid="create-connection-form"
      id="create-connection-form"
    >
      <ConnectionHeader appId="bazaarvoice" title={t('title')} />
      <Input
        type="text"
        name="sftpServer.host"
        label={t('labelSftpServerHost')}
        register={register}
        control={control}
      />

      <Input
        type="number"
        name="sftpServer.port"
        label={t('labelSftpServerPort')}
        register={register}
        control={control}
      />

      <Input
        type="text"
        name="sftpServer.username"
        label={t('labelSftpServerUsername')}
        register={register}
        control={control}
      />

      <Input
        type="text"
        name="sftpServer.password"
        label={t('labelSftpServerPassword')}
        register={register}
        control={control}
      />

      <div className="flex justify-between">
        <Button
          data-testid="create-digital-asset-collection-submit-btn"
          type="submit"
          form="create-connection-form"
          loading={isLoading}
        >
          {connectionToEdit ? t('buttonReconnect') : t('buttonCreate')}
        </Button>

        {showCancelBtn && (
          <Button
            type="button"
            variant="secondary"
            className="flex gap-2"
            onClick={() => onClose?.(null)}
          >
            {t('buttonCancel')}
          </Button>
        )}
      </div>
    </form>
  );
};

export default BazaarvoiceEditConnectionComponent;
